import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useTenantId } from './useTenant';

/**
 * Driver administration: create, edit, deactivate, rotate keys, and the
 * Telegram onboarding QR.
 *
 * Two of these return something the admin can only ever see once, which
 * shapes how the UI has to handle them: creating a driver and rotating a key
 * both hand back a fresh API key in the response body and never again. The
 * components show it in a modal the admin must dismiss deliberately, rather
 * than in a toast that could vanish before it is copied.
 */

function useDriverInvalidation() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['drivers', tenantId] });
    // Deleting or renaming a driver changes what the orders table shows in
    // its assignment column, so that cache is stale too.
    queryClient.invalidateQueries({ queryKey: ['orders', tenantId] });
  };
}

// Must stay in the same order and spelling as core/utils.py's
// DRIVER_CHANNELS — the backend validates against that tuple and rejects
// anything else with a 400, so a channel that exists only here would be an
// option the dispatcher can pick and never save.
export const DRIVER_CHANNELS = ['whatsapp', 'sms', 'telegram', 'pwa'];

// Must stay in sync with core/utils.py's DRIVER_LICENSE_CATEGORIES — same
// reasoning as DRIVER_CHANNELS above.
export const LICENSE_CATEGORIES = ['B', 'BE', 'C1', 'C1E', 'C', 'CE'];

export function useSaveDriver() {
  const tenantId = useTenantId();
  const invalidate = useDriverInvalidation();
  return useMutation({
    mutationFn: ({ driverId, ...payload }) =>
      driverId
        ? api(`/api/drivers/${driverId}`, { method: 'PUT', body: payload })
        : api(`/api/tenants/${tenantId}/drivers`, { method: 'POST', body: payload }),
    onSuccess: invalidate,
  });
}

/**
 * Soft delete — the backend deactivates rather than removing, because a
 * driver's name still has to render on every historical order they ran.
 */
export function useDeleteDriver() {
  const invalidate = useDriverInvalidation();
  return useMutation({
    mutationFn: (driverId) => api(`/api/drivers/${driverId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/**
 * Un-archives a driver — the same {"active": true} PUT the edit form's
 * toggle would send, just without needing to open the edit form for it.
 * Their old API key stays revoked (see blueprints/drivers.py's
 * update_driver()): rotate-key is the deliberate way back to a working one.
 */
export function useRestoreDriver() {
  const invalidate = useDriverInvalidation();
  return useMutation({
    mutationFn: (driverId) =>
      api(`/api/drivers/${driverId}`, { method: 'PUT', body: { active: true } }),
    onSuccess: invalidate,
  });
}

export function useRotateDriverKey() {
  const invalidate = useDriverInvalidation();
  return useMutation({
    mutationFn: (driverId) => api(`/api/drivers/${driverId}/rotate-key`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

/**
 * A fresh Driver App password, returned exactly once.
 *
 * The counterpart to useRotateDriverKey above, and what makes a randomly
 * generated password workable: before this route existed there was no
 * recovery path at all, which is the pressure that produced the old
 * guessable "<full name>2026" scheme.
 */
export function useResetDriverPassword() {
  const invalidate = useDriverInvalidation();
  return useMutation({
    mutationFn: (driverId) => api(`/api/drivers/${driverId}/reset-password`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

/**
 * The onboarding QR: a deep link into the bot that links this driver's
 * Telegram account on /start. Returns both the URL and a ready-to-render SVG,
 * so the modal does not need a QR library. Only fetched when the modal opens
 * — it 400s when TELEGRAM_BOT_USERNAME is unset, which is a real and common
 * state on a fresh install and must not error the whole driver list.
 */
export function useDriverTelegramQr(driverId) {
  return useQuery({
    queryKey: ['driver', driverId, 'telegram-qr'],
    queryFn: () => api(`/api/drivers/${driverId}/telegram-qr`),
    enabled: Boolean(driverId),
    retry: false,
    staleTime: Infinity,
  });
}
